import {
  MealType,
  RecipeIngredientsGroup,
  RecordOption,
} from '@akin-home/amplify';
import { RecordWithImageFields } from '@akin-home/images';
import { OptionModel } from '@akin-home/options';
import { HasID } from '@akin-home/records';

export interface Recipe extends HasID, RecordWithImageFields {
  name: string;
  habitatID?: string;
  emoji?: string;
  /** A short description or introduction to the dish */
  description?: string;

  /**
   * Indicates whether the recipe is still in progress.
   * In progress recipes should only be viewable by content creators
   */
  inProgress?: boolean;

  dishType?: OptionModel;
  prepTime?: OptionModel;

  skillLevel?: OptionModel;
  servingSize?: OptionModel;

  cuisineType?: OptionModel;

  textureType?: OptionModel;

  familyfriendlyType?: OptionModel;

  specialDiets?: OptionModel[];
  /** This field is for keeping record of the special diet m2m records */
  specialDietM2mItems?: RecordOption[];

  specialDietIDs?: string[];

  prepNotes?: PrepNote[];
  mainIngredient?: OptionModel;
  /** Ingredients for the recipe */
  ingredients: RecipeIngredient[];

  readGroups?: string[];

  /** Simple list of strings for utensils */
  utensils: string[];

  /** Health rating 1-5 */
  health?: HealthRatingID;

  /** Budget rating 1-5 */
  budget?: BudgetRatingID;

  allergyIDs: string[];

  mealTypes: MealType[];
  distToUserPref?: number;
  allergens?: string[];

  dietary_balance?: string;

  default_servings?: number;

  createdAt?: string;
  updatedAt?: string;
}

export interface PrepNote {
  id: string;
  description: string;
  title?: string;
  icon?: PrepNoteIcon;
}

export enum PrepNoteIcon {
  bake,
  bowl,
  chopping_table,
  cook_chicken,
  fry_pan,
  serve,
}

export interface Ingredient {
  id: string;
  quantity: string;
  unitOfMeasure: string;
  name: string;
  category: string;

  /** ID of the DB ingredient */
  ingredientID?: string;

  /** Indicates that this ingredient is a pantry item */
  pantryItem: boolean;
}

/** An ingredient on a recipe */
export interface RecipeIngredient extends HasID {
  name: string;
  quantity: string;
  unitOfMeasure: string;
  pantryItem: boolean;
  ingredientID?: string;
}

export interface RecipeIngredientsGroupModel extends RecipeIngredientsGroup {
  id: string;
}

/** Avoidances */
export enum MealAvoidanceCategory {
  GLUTEN = 'Gluten',
  DAIRY = 'Dairy',
  SEAFOOD = 'Seafood',
  RED_MEAT = 'Red meat',
  FAT = 'Fat',
  CARBS = 'Carbs',
}

export enum MealAvoidanceDegree {
  ALL_OF_THE_TIME,
  SOME_OF_THE_TIME,
}

export interface MealAvoidance {
  /** The value of the meal avoidance */
  category: MealAvoidanceCategory;
  /** Which option is selected on the meal avoidance */
  degree: MealAvoidanceDegree;
}

export interface MealPlanPreferences {
  people?: number;
  budget?: number;
  time_and_effort?: number;
  dietary_balance?: number;
  allergies?: string[];
  avoidances?: MealAvoidance[];
  eats_meat?: boolean;

  /** The types of meat the user eats */
  meats_eaten?: MeatType[];

  /** Selected meal types to be included in the meal plan */
  meals_to_plan?: MealType[];

  /** Normal times for the meals to be planned. Must of format hh:mm */
  meal_times?: Partial<Record<MealType, string>>;

  /** Meals to be reminded about */
  reminders?: MealType[];
}

export interface Allergy {
  icon?: any; // IconProp
  image?: any;
  iconColor: string;
  backgroundColor: string;
  text: AllergyText;
}

export enum AllergyText {
  DAIRY = 'Dairy',
  EGGS = 'Eggs',
  WHEAT = 'Wheat',
  PEANUTS = 'Peanuts',
  TREE_NUTS = 'Treenuts',
  SHELLFISH = 'Shellfish',
  SOY = 'Soy',
  SESAME = 'Sesame',
}

export type MeatType = 'beef' | 'lamb' | 'pork' | 'fish' | 'chicken' | 'turkey';

export type HealthRatingID = 1 | 2 | 3;
export type BudgetRatingID = 1 | 2 | 3 | 4 | 5;
